import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, limit as qlimit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';

interface EntityNode {
  id: string;
  legalName: string;
  shortName?: string;
  type?: string;
  status?: string;
  parentEntityId?: string | null;
  docCount?: number;
}
interface Member {
  id: string;
  legalFirstName?: string;
  legalLastName?: string;
  preferredName?: string;
  relationshipToHead?: string;
}

const TYPE_COLOR: Record<string, string> = {
  LP: 'bg-[#1F3357] text-white',
  LLC: 'bg-[#2F4F3D] text-white',
  Trust: 'bg-[#583E5E] text-white',
  Corp: 'bg-[#9C4A3A] text-white',
  Individual: 'bg-[#B8924A] text-white',
  Other: 'bg-stone-400 text-white',
};

/**
 * Shulkin Family org chart — the entity hierarchy (built from parentEntityId)
 * plus the family directory. Reassign an entity's parent inline to shape the
 * tree; everything else flows from the entity master.
 */
export function OrgChart() {
  const { role } = useAuth();
  const [entities, setEntities] = useState<EntityNode[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const u1 = onSnapshot(query(collection(db, 'entities'), qlimit(500)), (snap) =>
      setEntities(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<EntityNode, 'id'>) }))),
    );
    const u2 = onSnapshot(
      query(collection(db, 'familyMembers'), qlimit(200)),
      (snap) => setMembers(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Member, 'id'>) }))),
      () => setMembers([]),
    );
    return () => { u1(); u2(); };
  }, []);

  const active = useMemo(() => entities.filter((e) => e.status !== 'dissolved'), [entities]);

  // Build a forest from parentEntityId. Entities with no/unknown parent are roots.
  const { roots, childrenOf } = useMemo(() => {
    const byId = new Map(active.map((e) => [e.id, e]));
    const childrenOf = new Map<string, EntityNode[]>();
    const roots: EntityNode[] = [];
    for (const e of active) {
      const pid = e.parentEntityId && byId.has(e.parentEntityId) ? e.parentEntityId : null;
      if (pid) {
        const arr = childrenOf.get(pid) ?? [];
        arr.push(e);
        childrenOf.set(pid, arr);
      } else {
        roots.push(e);
      }
    }
    const sortFn = (a: EntityNode, b: EntityNode) => (a.legalName ?? '').localeCompare(b.legalName ?? '');
    roots.sort(sortFn);
    for (const arr of childrenOf.values()) arr.sort(sortFn);
    return { roots, childrenOf };
  }, [active]);

  if (role !== 'admin') {
    return <div className="mx-auto max-w-xl px-6 py-12 text-sm text-stone-500">Admins only.</div>;
  }

  async function setParent(entityId: string, parentEntityId: string) {
    setBusy(true);
    try {
      const e = active.find((x) => x.id === entityId);
      await httpsCallable(functions, 'setEntity')({
        id: entityId,
        legalName: e?.legalName,
        shortName: e?.shortName,
        type: e?.type ?? 'Other',
        parentEntityId: parentEntityId || undefined,
        status: 'active',
      });
    } finally {
      setBusy(false);
    }
  }

  const renderNode = (e: EntityNode, depth: number): React.ReactNode => {
    const kids = childrenOf.get(e.id) ?? [];
    return (
      <div key={e.id} className={depth > 0 ? 'ml-5 border-l border-stone-200 pl-4' : ''}>
        <div className="flex items-center gap-2 py-1.5">
          <span className={`grid h-7 min-w-[28px] place-items-center rounded px-1.5 text-[10px] font-bold ${TYPE_COLOR[e.type ?? 'Other'] ?? TYPE_COLOR.Other}`}>
            {e.type ?? '—'}
          </span>
          <Link to={`/documents?entity=${encodeURIComponent(e.legalName)}`} className="text-sm font-medium text-stone-800 hover:underline">
            {e.legalName}
          </Link>
          {typeof e.docCount === 'number' && e.docCount > 0 && (
            <span className="rounded-full bg-stone-100 px-1.5 text-[10px] text-stone-500">{e.docCount} docs</span>
          )}
          <select
            value={e.parentEntityId ?? ''}
            disabled={busy}
            onChange={(ev) => setParent(e.id, ev.target.value)}
            className="ml-auto rounded border border-stone-200 px-1 py-0.5 text-[10px] text-stone-500"
            title="Parent entity"
          >
            <option value="">— no parent (top) —</option>
            {active.filter((o) => o.id !== e.id).map((o) => (
              <option key={o.id} value={o.id}>↳ under {o.legalName}</option>
            ))}
          </select>
        </div>
        {kids.map((k) => renderNode(k, depth + 1))}
      </div>
    );
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-400">Module 2</div>
          <h1 className="text-2xl font-semibold text-stone-800">Shulkin Family org chart</h1>
          <p className="text-sm text-stone-500">
            The entity hierarchy plus the family. Set each entity's parent to shape the tree — manage
            the master in <Link to="/entities" className="text-[color:var(--sfp-primary)] hover:underline">Entities</Link>.
          </p>
        </div>
      </header>

      <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-stone-800">Entity structure ({active.length})</h2>
        <div className="mt-3">
          {roots.length === 0 ? (
            <div className="py-6 text-center text-xs text-stone-400">
              No entities yet. Seed them in <Link to="/entities" className="text-[color:var(--sfp-primary)] hover:underline">Entities</Link>.
            </div>
          ) : (
            roots.map((r) => renderNode(r, 0))
          )}
        </div>
      </section>

      <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-stone-800">Family members ({members.length})</h2>
          <Link to="/admin/family" className="text-xs text-[color:var(--sfp-primary)] hover:underline">Manage →</Link>
        </div>
        {members.length === 0 ? (
          <div className="mt-3 py-4 text-center text-xs text-stone-400">
            No family members yet. Add them in <Link to="/admin/family" className="text-[color:var(--sfp-primary)] hover:underline">Family members</Link>.
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {members.map((m) => (
              <Link
                key={m.id}
                to={`/admin/family/${m.id}`}
                className="flex items-center gap-2 rounded-xl border border-stone-200 px-3 py-2 hover:bg-stone-50"
              >
                <span className="grid h-8 w-8 place-items-center rounded-full bg-[#B8924A] text-xs font-bold text-white">
                  {(m.preferredName || m.legalFirstName || '?').slice(0, 1)}
                  {(m.legalLastName || '').slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-medium text-stone-800">
                    {[m.preferredName || m.legalFirstName, m.legalLastName].filter(Boolean).join(' ') || 'Unnamed'}
                  </span>
                  {m.relationshipToHead && (
                    <span className="block text-[10px] uppercase tracking-wider text-stone-400">{m.relationshipToHead.replace(/_/g, ' ')}</span>
                  )}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
